import { markAutomaticReload } from "./automaticReload";

const DEPLOYMENT_CHECK_INTERVAL_MS = 5 * 60_000;
const DEPLOYMENT_META_NAME = "opstrack-deployment-id";

interface DeploymentVersion {
  deploymentId?: unknown;
}

export function isNewDeployment(runningDeploymentId: string, latestDeploymentId: string): boolean {
  return Boolean(
    runningDeploymentId
    && latestDeploymentId
    && runningDeploymentId !== latestDeploymentId
  );
}

function getRunningDeploymentId(): string {
  return document
    .querySelector<HTMLMetaElement>(`meta[name="${DEPLOYMENT_META_NAME}"]`)
    ?.content.trim() || "";
}

async function getLatestDeploymentId(): Promise<string> {
  const response = await fetch(`/version.json?check=${Date.now()}`, {
    cache: "no-store",
    headers: { Accept: "application/json" }
  });
  if (!response.ok) throw new Error(`Version request failed with ${response.status}`);

  const version = await response.json() as DeploymentVersion;
  return typeof version.deploymentId === "string" ? version.deploymentId.trim() : "";
}

export function startDeploymentRefreshChecks(): () => void {
  const runningDeploymentId = getRunningDeploymentId();
  if (!runningDeploymentId) return () => undefined;

  let checking = false;
  let stopped = false;

  const check = async () => {
    if (checking || stopped || document.visibilityState === "hidden") return;
    checking = true;
    try {
      const latestDeploymentId = await getLatestDeploymentId();
      if (isNewDeployment(runningDeploymentId, latestDeploymentId)) {
        markAutomaticReload();
        window.location.reload();
      }
    } catch (error) {
      console.warn("Deployment version check failed:", error);
    } finally {
      checking = false;
    }
  };

  const checkWhenAvailable = () => { void check(); };
  const checkWhenVisible = () => {
    if (document.visibilityState === "visible") void check();
  };

  const interval = window.setInterval(checkWhenAvailable, DEPLOYMENT_CHECK_INTERVAL_MS);
  window.addEventListener("focus", checkWhenAvailable);
  window.addEventListener("online", checkWhenAvailable);
  window.addEventListener("pageshow", checkWhenAvailable);
  document.addEventListener("visibilitychange", checkWhenVisible);
  void check();

  return () => {
    stopped = true;
    window.clearInterval(interval);
    window.removeEventListener("focus", checkWhenAvailable);
    window.removeEventListener("online", checkWhenAvailable);
    window.removeEventListener("pageshow", checkWhenAvailable);
    document.removeEventListener("visibilitychange", checkWhenVisible);
  };
}
